import React, { useRef, useMemo, useState, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight, FaWhatsapp } from 'react-icons/fa';
import CotizadorCard from './CotizadorCard';
import useSlugUrl from "~/common/hooks/useSlugUrl";
const calcularMensualidad = (precio, tasaAnual, meses, montoEnganche) => {
  const p = Number(precio) || 0;
  const e = Number(montoEnganche) || 0;
  const t = Number(tasaAnual);
  
  const capital = p - e;
  if (capital <= 0) return 0;

  const r = (t / 100) / 12;
  const cuota = capital * ( (r * Math.pow(1 + r, meses)) / (Math.pow(1 + r, meses) - 1) );
  
  return cuota;
};

const CardsCotizador = ({ auto,slug }) => {
  const scrollRef = useRef(null);
  const anioActual = new Date().getFullYear();

  // --- SOLUCIÓN ERROR HIDRATACIÓN (WhatsApp) ---
  // Inicializamos con un valor fijo y lo cambiamos solo en el cliente mediante useEffect
  const [phoneOficina, setPhoneOficina] = useState("6621940563");

  useEffect(() => {
    // Esto solo se ejecuta en el navegador, evitando el desajuste con el servidor
    const random = Math.random();
    if (random < 0.65) {
      setPhoneOficina("6621940563");
    } else {
      setPhoneOficina("6221001003");
    }
  }, [auto?.id]); // Se relanza específicamente si cambia el auto

  // 1. VALIDACIÓN INICIAL Y DE PRECIO (Evita fallos en autos Negociables)
  if (!auto || !auto.price || Number(auto.price) === 0) return null;

  const price = Number(auto.price) || 0;
  const year = auto.year || "";
  const engancheSugerido = auto.engancheSugerido;
  const tasaInteres = auto.tasaInteres;
  
  const nombreCompletoAuto = auto.title + " " + auto.year;  
  const nombreCompletoAutowithid = auto.title + " " + auto.year + " -" + auto.id;

  // 2. LÓGICA DE NEGOCIO (Tasas y Enganches)
  // Usamos auto.id como dependencia para limpiar el estado al navegar
  const configFinanciera = useMemo(() => {
    let tasaFinal = Number(tasaInteres);
    if (!tasaFinal || tasaFinal === 0) {
      tasaFinal = 13.99; // Tasa por defecto
    }

    let engancheFinal = Number(engancheSugerido);
    if (!engancheFinal || engancheFinal === 0) {
      engancheFinal = price * 0.2999;
    }

    const diferenciaAnios = anioActual - year;
    const plazoMaximo = 120 - (diferenciaAnios * 12);
    const todosLosPlazos = [12, 24, 36, 48, 60, 72, 84, 96, 108, 120];
    const plazosFiltrados = todosLosPlazos.filter(p => p <= plazoMaximo);

    return { tasaFinal, engancheFinal, plazosFiltrados };
  }, [auto.id, price, year, tasaInteres, engancheSugerido, anioActual]);

  const { tasaFinal, engancheFinal, plazosFiltrados } = configFinanciera;

  const { path } = useSlugUrl({
      id: auto.id,
      slug: slug,
    });

    // Genera la URL completa
  const pageUrl = `https://seminuevossonora.com${path}`;
  
  // 3. CONFIGURACIÓN WHATSAPP
  const mensajeWa = `Buen día, vi el financiamiento en seminuevossonora.mx para el auto ${nombreCompletoAuto}. Me interesa recibir más información: ${pageUrl}`;
  const whatsappLink = `https://wa.me/52${phoneOficina}?text=${encodeURIComponent(mensajeWa)}`;

  const scroll = (dir) => {
    const { current } = scrollRef;
    if (current) {
      const move = dir === 'left' ? -current.clientWidth : current.clientWidth;
      current.scrollBy({ left: move, behavior: 'smooth' });
    }
  };

  if (plazosFiltrados.length === 0) return null;

  return (
    <section className="my-10 relative group px-4">
      {/* ENCABEZADO */}
      <div className="mb-6">
        <h2 className="text-2xl font-black text-gray-800 uppercase italic tracking-tighter">
          Planes de Financiamiento para {nombreCompletoAuto}
          <span className="block text-[10px] font-medium text-slate-400 normal-case not-italic tracking-normal mt-1">
            Sujeto a aprobación de crédito
          </span>
        </h2>
        <div className="flex flex-wrap gap-2 mt-3">
          <span className="text-[10px] bg-slate-100 text-slate-600 font-bold px-2 py-1 rounded border border-slate-200 uppercase">
            Tasa Aplicada: {tasaFinal}%
          </span>
          <span className="text-[10px] bg-slate-100 text-slate-600 font-bold px-2 py-1 rounded border border-slate-200 uppercase">
            Pago Inicial: ${engancheFinal.toLocaleString('es-MX', { maximumFractionDigits: 0 })}
          </span>
        </div>
      </div>
      
      {/* CARRUSEL DE TARJETAS */}
      <div className="relative">
        <button 
          onClick={() => scroll('left')} 
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/95 shadow-xl p-3 rounded-full hidden md:block hover:bg-yellow-500 hover:text-white transition-all border border-gray-100"
        >
          <FaChevronLeft size={18}/>
        </button>
        
        <div 
          ref={scrollRef} 
          className="flex overflow-x-auto py-6 gap-6 snap-x snap-mandatory scrollbar-hide scroll-smooth"
        >
          {plazosFiltrados.map((m) => (
            <CotizadorCard 
              key={`${auto.id}-${m}`} 
              meses={m} 
              pago={calcularMensualidad(price, tasaFinal, m, engancheFinal)} 
            />
          ))}
        </div>

        <button 
          onClick={() => scroll('right')} 
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/95 shadow-xl p-3 rounded-full hidden md:block hover:bg-yellow-500 hover:text-white transition-all border border-gray-100"
        >
          <FaChevronRight size={18}/>
        </button>
      </div>

      {/* BOTÓN DE CONTACTO WHATSAPP */}
      <div className="mt-1 mb-1 flex flex-col items-center justify-center border-t border-b border-gray-100 py-8">
        <p className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-tight text-center">
          *** ¿Te interesa crédito? Da clic aquí
        </p>
        
        <a
          href={whatsappLink}
          target="_blank"
          rel="noreferrer"
          className="bg-[#25D366] hover:bg-[#128C7E] text-white flex gap-2 items-center justify-center px-10 py-4 rounded-full transition-all hover:scale-105 shadow-lg w-full md:w-auto font-black uppercase text-sm"
        >
          <FaWhatsapp fontSize={22} />
          Contactar a Oficina
        </a>
        <p className="mt-3 text-[11px] text-gray-400 font-medium">Número de oficina: {phoneOficina}</p>
      </div>

      {/* TEXTO LEGAL */}
      <div className="mt-1 bg-amber-50/50 border-l-4 border-amber-400 p-5 rounded-r-lg">
        <div className="flex flex-col gap-2">
          <p className="text-[11px] leading-relaxed text-slate-700 font-bold uppercase">
            *** Cotizaciones sujetas a cambios de acuerdo a tu buró de crédito.
          </p>
          <p className="text-[11px] leading-relaxed text-slate-700 font-bold uppercase">
            *** Sujeto a cambios sin previo aviso.
          </p>
          <hr className="my-1 border-amber-200" />
          <p className="text-[10px] leading-relaxed text-slate-500 italic">
            Los valores presentados son únicamente de carácter informativo y referencial. 
            Las mensualidades pueden variar dependiendo del historial crediticio del solicitante.
          </p>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
};

export default CardsCotizador;